// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useRef } from 'react';
import { Checkbox, InlineNotification, SkeletonPlaceholder, Tile } from '@carbon/react';
import { useShellStore, yAxisKey } from '../../../stores/shellStore';
import PlanTimeSeriesChart from '../../../components/charts/PlanTimeSeriesChart';
import type { ChartCursor } from '../../../components/charts/PlanTimeSeriesChart';
import type { PhaseBand, PlanChartSpec, SeriesVariant } from '../../../utils/planSeries';

const LIVE_COLOR = 'var(--st-ok)';

const LEGEND_COLORS: Partial<Record<SeriesVariant, string>> = { live: LIVE_COLOR };

/** Legend swatch per variant. Live reads as a solid green line, baseline dashed. */
function LegendItem({
  variant,
  label,
  accent,
}: {
  variant: SeriesVariant;
  label: string;
  accent: string;
}) {
  const color = LEGEND_COLORS[variant] ?? accent;
  return (
    <span className="live-legend-item">
      <span
        className={`live-legend-dash${variant === 'baseline' ? ' live-legend-dash--baseline' : ''}`}
        style={variant === 'baseline' ? undefined : { borderTopColor: color }}
        aria-hidden
      />
      {label}
    </span>
  );
}

/**
 * The default fixed range: zero up to the forecast's peak, rounded up to a
 * whole hundred kW. Falls back to every series when there is no forecast curve.
 */
function defaultRange(spec: PlanChartSpec): { min: number; max: number } | null {
  const forecast = spec.series.filter(s => s.variant !== 'live');
  const pool = forecast.length ? forecast : spec.series;
  const values = pool.flatMap(s => s.values).filter((v): v is number => Number.isFinite(v));
  if (values.length === 0) return null;
  const peak = Math.max(...values);
  return { min: 0, max: Math.max(100, Math.ceil(peak / 100) * 100) };
}

export interface LiveChartCardProps {
  title: string;
  /** With a site, kW cards get a persisted, fixed y-axis range and its control. */
  siteId?: string;
  /** Forecast/measured series to draw; undefined renders the `absentNote`. */
  spec?: PlanChartSpec;
  accent: string;
  bands?: PhaseBand[];
  hourTicks?: { index: number; label: string }[];
  hoverLabelAt?: (_index: number) => string;
  cursor?: ChartCursor;
  loading?: boolean;
  /**
   * The measured curve has not arrived yet, but the forecast has.
   *
   * Without this the two states are indistinguishable: `withLiveSeries` drops an
   * all-null measured series rather than drawing an empty one, and the skeleton
   * below only fires when there is no spec at all — which never happens once the
   * plan's own forecast exists. So a slow telemetry fetch rendered as a finished
   * card that silently omitted the live line, and read as the line disappearing.
   */
  liveLoading?: boolean;
  /**
   * Why there is nothing to draw. Required in spirit: a card with no series must
   * say why rather than render an empty frame.
   */
  absentNote?: string;
  /** Shown under a drawn chart, e.g. partial live coverage. */
  footnote?: string;
}

function LiveChartCard({
  title,
  siteId,
  spec,
  accent,
  bands,
  hourTicks,
  hoverLabelAt,
  cursor,
  loading,
  liveLoading,
  absentNote,
  footnote,
}: LiveChartCardProps) {
  // Only worth saying while the forecast is on screen without it; with no spec
  // the absent-note branch below already speaks.
  const pendingMeasured = Boolean(liveLoading && spec);

  const rangeable = Boolean(siteId && spec && spec.unit === 'kW');
  const key = siteId ? yAxisKey(siteId, title) : '';
  const pref = useShellStore(s => s.yAxisPrefs[key]);
  const setPref = useShellStore(s => s.setYAxisPref);
  // Derived once, from the first plan that arrives, so data landing later never
  // moves the axis. A stored preference wins over it.
  const derived = useRef<{ min: number; max: number } | null>(null);
  if (rangeable && !derived.current && spec) derived.current = defaultRange(spec);
  const hasStored = pref?.min !== undefined && pref?.max !== undefined;
  const range =
    !rangeable || pref?.fit
      ? undefined
      : hasStored
        ? { min: pref.min as number, max: pref.max as number }
        : (derived.current ?? undefined);

  const commit = (edge: 'min' | 'max', raw: string) => {
    const next = Number(raw);
    if (raw.trim() === '' || !Number.isFinite(next) || !range) return;
    const merged = { min: range.min, max: range.max, [edge]: next };
    if (merged.min >= merged.max) return;
    setPref(key, { fit: false, ...merged });
  };
  return (
    <Tile className="live-chart-card">
      <div className="live-chart-head">
        <h4 className="live-chart-title">{title}</h4>
        {spec && (
          <div className="live-legend">
            {spec.series.map(s => (
              <LegendItem key={s.label} variant={s.variant} label={s.label} accent={accent} />
            ))}
            {pendingMeasured && (
              <span className="live-legend-item live-legend-item--pending">
                <span className="live-legend-spinner" aria-hidden />
                Measured loading…
              </span>
            )}
          </div>
        )}
      </div>

      {rangeable && (
        <div className="live-chart-range">
          <Checkbox
            id={`fit-${key}`}
            labelText="Fit to data"
            checked={Boolean(pref?.fit)}
            onChange={(_, { checked }) => setPref(key, { ...pref, fit: checked })}
          />
          {range && (
            <>
              {(['min', 'max'] as const).map(edge => (
                <label key={edge} className="live-chart-range-field">
                  {edge === 'min' ? 'Min kW' : 'Max kW'}
                  <input
                    type="number"
                    // Remount on a stored change so the field shows the committed value.
                    key={`${edge}-${range[edge]}`}
                    defaultValue={range[edge]}
                    onBlur={e => commit(edge, e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') commit(edge, e.currentTarget.value);
                    }}
                  />
                </label>
              ))}
            </>
          )}
        </div>
      )}

      {loading && !spec ? (
        <SkeletonPlaceholder className="live-chart-skeleton" />
      ) : spec ? (
        <PlanTimeSeriesChart
          series={spec.series}
          unit={spec.unit}
          accent={accent}
          bands={bands}
          hourTicks={hourTicks}
          hoverLabelAt={hoverLabelAt}
          cursor={cursor}
          yRange={range}
          ariaLabel={`${title} over the plan's day`}
        />
      ) : (
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          statusIconDescription="Information"
          // The card heading right above already names the signal; repeating it
          // inside the notification reads as a stutter.
          title=""
          subtitle={absentNote ?? 'No data is available for this series yet.'}
          className="live-chart-absent"
        />
      )}

      {spec && (footnote || pendingMeasured) && (
        <p className="live-chart-footnote">
          {pendingMeasured ? 'Waiting on the first measured reading for this window…' : footnote}
        </p>
      )}
    </Tile>
  );
}

// Memoized so a clock tick that changes nothing about this card stops short of
// the chart underneath it.
export default React.memo(LiveChartCard);
